import React, { useState, useMemo, useImperativeHandle, forwardRef } from "react";
import { TextField, List, ListItem, ListItemText, InputAdornment, Grid } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from "prop-types";
import { isFunction, valueFoundInArray } from "../../utils";
import { useTranslation } from "react-i18next";

const CompSearchInputNew = forwardRef((props, ref) => {
    const { t } = useTranslation('translation');
    const {
        rows = [],
        selectedRows,
        keyColumn,
        valueColumn,
        className,
        multiselect,
        onSelect,
        isLabelShrink,
        onChange,
        disabled,
        permanentSelections = [],
        label = "",
        error,
        multiSelectDelete,
        hasPermissionToEdit,
        activeUserId,
        ...componentProps
    } = props;
    
    const translatedError = error ? t(`errors.${error}`) : null;

    const [searchText, setSearchText] = useState('');

    const closeSearchList = () => setSearchText('');

    useImperativeHandle(ref, () => ({
        closeSearchList,
    }));

    const handleSelectionToggle = (event, row, index) => {
        event.stopPropagation();
        event.preventDefault();
        if (disabled) return;

        const i = selectedRows.indexOf(row[keyColumn])
        const isAddAction = !isSelected(row[keyColumn]);
        setSearchText('');

        if (isFunction(onSelect)) {
            onSelect(row, isAddAction, index, i);
        }
    };

    const getValueColumn = (row) => {
        if (Array.isArray(valueColumn)) {
            return valueColumn.map(columnKey => {
                if (Array.isArray(columnKey)) {
                    const obj = columnKey[0].find(o => o.id === row[columnKey[1]]);
                    return obj ? obj.name : "";
                }
                return row[columnKey];
            }).join(" ");
        }
        return row[valueColumn] || "";
    };

    const handleSearchChange = (e) => {
        if (disabled) return;
        setSearchText(e.target.value);
        if (isFunction(onChange)) {
            onChange(e);
        }
    };

    const isSelected = (value) => selectedRows.includes(value);

    const filteredRows = useMemo(() => {
        const lowerCasedFilter = searchText.toLowerCase();
        return rows.filter(item =>
            !isSelected(item[keyColumn]) &&
            Object.keys(item).some(key =>
                typeof item[key] === 'string' &&
                item[key].toLowerCase().includes(lowerCasedFilter)
            )
        );
    }, [searchText, rows, selectedRows, keyColumn]);

    const selectedRowsList = useMemo(() => multiselect && rows.filter(item => isSelected(item[keyColumn])), [multiselect, rows, selectedRows, keyColumn]);

    return (
        <div className={`search-input ${className} ${translatedError ? 'error' : ''} ${disabled ? 'disabled' : ''}`} {...componentProps}>
            {/* <div className="label">{label}</div> */}
            <TextField
                variant="outlined"
                className="search-input-input"
                disabled={disabled}
                value={searchText}
                label={label}
                onChange={handleSearchChange}
                error={!!translatedError}
                helperText={translatedError}
                slotProps={{
                    inputLabel: {
                        shrink: isLabelShrink,
                    },
                    input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <SearchIcon color={disabled ? "disabled" : "action"} />
                        </InputAdornment>
                    ),
                    },
                }}
                sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                        height: 39,
                    },
                    "& .MuiInputBase-input": {
                        height: "100%",
                        padding: "0 14px",
                        boxSizing: "border-box"
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#404CE2" //fix in the future, some scss variable thingy wrong
                    }
                }}
            />
            {searchText && filteredRows.length > 0 && (
                <div>
                    <div className="searchOverlay" onClick={closeSearchList} />
                    <List className="search-list-container">
                        {filteredRows.map((row, index) => (
                            <ListItem
                                key={`search-input-list-item-${index}`}
                                role={undefined}
                                dense
                                disabled={disabled}
                                button
                                className={`search-list-item ${valueFoundInArray(row[keyColumn], selectedRows) ? 'selected' : ''}`}
                                onClick={e => handleSelectionToggle(e, row, index)}
                            >
                                <ListItemText id={`search-list-item-label-${row[keyColumn]}`} primary={getValueColumn(row)} />
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}
        <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
            {multiselect && selectedRowsList.length > 0 && (
                <ul className="selected-list">
                    {selectedRowsList.map((row, index) => (
                        <li className="item" key={`selected-list-item-${index}`}>
                            <div>{getValueColumn(row)}
                                {row[keyColumn] !== activeUserId && (
                                    <div
                                        className={`closeButton ${valueFoundInArray(row[keyColumn], permanentSelections) ? 'hidden' : ''} ${!hasPermissionToEdit ? 'disabled' : ''}`}
                                        onClick={e => hasPermissionToEdit ? handleSelectionToggle(e, row, index) : null}                                    />
                                )}
                            </div>
                        </li>
                    ))}
                    <div className="clear" />
                </ul>
            )}
        </Grid>
        </div>
    );
});

CompSearchInputNew.propTypes = {
    keyColumn: PropTypes.string,
    valueColumn: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.any)
    ]),
    rows: PropTypes.array,
    selectedRows: PropTypes.array,
    multiselect: PropTypes.bool,
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.string
};

export { CompSearchInputNew };